import { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Progress } from '../../ui/progress';
import {
  Truck,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  X,
  Save,
  Package,
  User,
  FileText,
  Printer,
  ClipboardCheck,
  RefreshCw,
  Loader2
} from 'lucide-react';
import {
  procurementService,
  PurchaseOrder,
  PurchaseOrderItem,
  ReceivingOrderSummary,
  ReceivingOrderWithItems,
  ReceivingOrderCreate,
  ReceivingOrderItemCreate,
  ReceivingStatus,
  QualityStatus
} from '../../../src/services/procurement.service';
import { authService } from '../../../src/services/auth.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useWarehouses } from '../../../src/hooks/queries/use-inventory-items';

// ─── GRN Modal ────────────────────────────────────────────────────────────────

interface GRNModalProps {
  mode: 'add' | 'edit' | 'view';
  grn?: ReceivingOrderSummary | ReceivingOrderWithItems;
  trigger: React.ReactNode;
  onSaved?: () => void;
}

function GRNModal({ mode, grn, trigger, onSaved }: GRNModalProps) {
  const isReadOnly = mode === 'view';
  const { orgId } = useAuth();
  const { data: warehouses = [] } = useWarehouses(orgId);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [loadingGRN, setLoadingGRN] = useState(false);

  // Available POs (sent or acknowledged)
  const [availablePOs, setAvailablePOs] = useState<PurchaseOrder[]>([]);

  // Full GRN detail (for edit/view loaded from API)
  const [grnDetail, setGrnDetail] = useState<ReceivingOrderWithItems | null>(null);

  // Selected PO (to populate line items)
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);

  const [formData, setFormData] = useState({
    purchaseOrderId: '',
    vendorId: '',
    vendorName: '',
    poNumber: '',
    deliveryDate: '',
    deliveryNoteNumber: '',
    packingSlipNumber: '',
    carrier: '',
    trackingNumber: '',
    deliveryLocationId: '',
    warehouseId: '',
    inspectionRequired: false,
    qualityStatus: 'pending' as QualityStatus,
    specialInstructions: '',
    notes: '',
    items: [] as Array<{
      line_number: number;
      po_item_id?: string;
      item_code: string;
      item_description: string;
      expected_quantity: number;
      received_quantity: number;
      accepted_quantity: number;
      rejected_quantity: number;
      unit_of_measure: string;
      unit_cost?: number;
      inspection_result: string;
      quality_notes: string;
    }>
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Load eligible POs when modal opens in add mode
  useEffect(() => {
    if (open && mode === 'add') {
      loadEligiblePOs();
    }
    if (open && (mode === 'edit' || mode === 'view') && grn) {
      loadGRNDetail();
    }
  }, [open, mode, grn]);

  const loadEligiblePOs = async () => {
    setLoadingPOs(true);
    try {
      await authService.getUserProfile();
      // Load POs that are sent or acknowledged (eligible for GRN)
      const [sentRes, acknowledgedRes, partialRes] = await Promise.all([
        procurementService.getPurchaseOrders(1, 200, { status: 'sent' }),
        procurementService.getPurchaseOrders(1, 200, { status: 'acknowledged' }),
        procurementService.getPurchaseOrders(1, 200, { status: 'partially_received' }),
      ]);

      const pos: PurchaseOrder[] = [];
      if (sentRes.data?.data) pos.push(...sentRes.data.data);
      if (acknowledgedRes.data?.data) pos.push(...acknowledgedRes.data.data);
      if (partialRes.data?.data) pos.push(...partialRes.data.data);

      // Deduplicate by po_id
      const unique = Array.from(new Map(pos.map(p => [p.po_id, p])).values());
      setAvailablePOs(unique);
    } catch {
      showError('Failed to load purchase orders');
    } finally {
      setLoadingPOs(false);
    }
  };

  const loadGRNDetail = async () => {
    if (!grn) return;
    setLoadingGRN(true);
    try {
      await authService.getUserProfile();
      const res = await procurementService.getReceivingOrder(
        (grn as ReceivingOrderSummary).receiving_order_id
      );
      if (res.data) {
        const detail = res.data;
        setGrnDetail(detail);
        setFormData({
          purchaseOrderId: detail.purchase_order_id || '',
          vendorId: detail.vendor_id,
          vendorName: detail.vendor_name || '',
          poNumber: detail.po_number || '',
          deliveryDate: detail.actual_delivery_date || detail.expected_delivery_date || '',
          deliveryNoteNumber: detail.delivery_note_number || '',
          packingSlipNumber: detail.packing_slip_number || '',
          carrier: detail.carrier || '',
          trackingNumber: detail.tracking_number || '',
          deliveryLocationId: '',
          warehouseId: detail.delivery_location_id || '',
          inspectionRequired: detail.inspection_required,
          qualityStatus: detail.quality_status,
          specialInstructions: detail.special_instructions || '',
          notes: detail.notes || '',
          items: detail.line_items.map(item => ({
            line_number: item.line_number,
            po_item_id: item.po_item_id,
            item_code: item.item_code || '',
            item_description: item.item_description,
            expected_quantity: Number(item.expected_quantity),
            received_quantity: Number(item.received_quantity),
            accepted_quantity: Number(item.accepted_quantity),
            rejected_quantity: Number(item.rejected_quantity),
            unit_of_measure: item.unit_of_measure,
            unit_cost: item.unit_cost ? Number(item.unit_cost) : undefined,
            inspection_result: item.inspection_result,
            quality_notes: item.quality_notes || '',
          }))
        });
      }
    } catch {
      showError('Failed to load GRN details');
    } finally {
      setLoadingGRN(false);
    }
  };

  const handlePOSelect = async (poId: string) => {
    const po = availablePOs.find(p => p.po_id === poId);
    if (!po) return;

    setSelectedPO(po);
    // Load full PO with items
    try {
      await authService.getUserProfile();
      const res = await procurementService.getPurchaseOrder(poId);
      const fullPO = res.data;
      if (!fullPO) return;

      setFormData(prev => ({
        ...prev,
        purchaseOrderId: fullPO.po_id,
        vendorId: fullPO.vendor_id,
        vendorName: fullPO.vendor_name,
        poNumber: fullPO.po_number,
        items: (fullPO.items || []).map((item: PurchaseOrderItem, idx: number) => ({
          line_number: idx + 1,
          po_item_id: item.po_item_id,
          item_code: item.item_code,
          item_description: item.item_name,
          expected_quantity: Number(item.quantity_ordered) - Number(item.quantity_received || 0),
          received_quantity: 0,
          accepted_quantity: 0,
          rejected_quantity: 0,
          unit_of_measure: item.unit_of_measure,
          unit_cost: Number(item.unit_price),
          inspection_result: 'not_required',
          quality_notes: '',
        }))
      }));
    } catch {
      showError('Failed to load PO details');
    }
  };

  const updateItem = (index: number, field: string, value: any) => {
    const updated = [...formData.items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, items: updated }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        line_number: prev.items.length + 1,
        item_code: '',
        item_description: '',
        expected_quantity: 0,
        received_quantity: 0,
        accepted_quantity: 0,
        rejected_quantity: 0,
        unit_of_measure: '',
        inspection_result: 'not_required',
        quality_notes: '',
      }]
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.vendorId) newErrors.purchaseOrderId = 'Purchase order is required';
    if (!formData.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (formData.items.length === 0) newErrors.items = 'At least one item is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await authService.getUserProfile();
      if (mode === 'add') {
        const payload: ReceivingOrderCreate = {
          vendor_id: formData.vendorId,
          purchase_order_id: formData.purchaseOrderId || undefined,
          expected_delivery_date: formData.deliveryDate || undefined,
          delivery_location_id: formData.warehouseId || formData.vendorId,
          delivery_note_number: formData.deliveryNoteNumber || undefined,
          packing_slip_number: formData.packingSlipNumber || undefined,
          carrier: formData.carrier || undefined,
          tracking_number: formData.trackingNumber || undefined,
          inspection_required: formData.inspectionRequired,
          special_instructions: formData.specialInstructions || undefined,
          notes: formData.notes || undefined,
          line_items: formData.items.map((item): ReceivingOrderItemCreate => ({
            line_number: item.line_number,
            po_item_id: item.po_item_id,
            item_code: item.item_code || undefined,
            item_description: item.item_description,
            expected_quantity: item.expected_quantity,
            unit_of_measure: item.unit_of_measure,
            unit_cost: item.unit_cost,
            notes: item.quality_notes || undefined,
          }))
        };
        await procurementService.createReceivingOrder(payload);
        showSuccess('Goods Received Note created successfully');
        const linkedPO = availablePOs.find(p => p.po_id === formData.purchaseOrderId);
        if (linkedPO?.created_by && linkedPO.po_number) {
          notificationService.grnCreated(linkedPO.created_by, 'GRN', linkedPO.po_number);
        }
      } else if (mode === 'edit' && grnDetail) {
        await procurementService.updateReceivingOrder(grnDetail.receiving_order_id, {
          delivery_note_number: formData.deliveryNoteNumber || undefined,
          packing_slip_number: formData.packingSlipNumber || undefined,
          carrier: formData.carrier || undefined,
          tracking_number: formData.trackingNumber || undefined,
          actual_delivery_date: formData.deliveryDate || undefined,
          inspection_required: formData.inspectionRequired,
          quality_status: formData.qualityStatus,
          special_instructions: formData.specialInstructions || undefined,
          notes: formData.notes || undefined,
        });
        // Update individual items received quantities
        for (const item of formData.items) {
          const original = grnDetail.line_items.find(li => li.line_number === item.line_number);
          if (original) {
            await procurementService.updateReceivingItem(original.receiving_item_id, {
              received_quantity: item.received_quantity,
              accepted_quantity: item.accepted_quantity,
              rejected_quantity: item.rejected_quantity,
              inspection_result: item.inspection_result as any,
              quality_notes: item.quality_notes || undefined,
            });
          }
        }
        showSuccess('Goods Received Note updated successfully');
      }
      setOpen(false);
      onSaved?.();
    } catch (err: any) {
      showError(err?.message || 'Failed to save GRN');
    } finally {
      setSaving(false);
    }
  };

  const getCompletionPercentage = (expected: number, received: number) => {
    if (expected === 0) return 0;
    return Math.min((received / expected) * 100, 100);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {mode === 'add' ? 'Create Goods Received Note' : mode === 'edit' ? 'Edit GRN' : 'GRN Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Record receipt of materials and supplies against a Purchase Order' :
              mode === 'edit' ? 'Update goods received information' :
                'View detailed goods received information'}
          </DialogDescription>
        </DialogHeader>

        {loadingGRN ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading GRN details...</span>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Receipt Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Receipt Information</h4>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* PO Select (add mode only) */}
                {mode === 'add' ? (
                  <div className="space-y-2">
                    <Label>Purchase Order *</Label>
                    {loadingPOs ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />Loading POs...
                      </div>
                    ) : (
                      <Select
                        value={formData.purchaseOrderId}
                        onValueChange={handlePOSelect}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a sent/acknowledged PO" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePOs.map(po => (
                            <SelectItem key={po.po_id} value={po.po_id}>
                              {po.po_number} – {po.vendor_name}
                              <span className="ml-2 text-xs text-muted-foreground capitalize">({po.status})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {errors.purchaseOrderId && <p className="text-sm text-destructive">{errors.purchaseOrderId}</p>}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Purchase Order</Label>
                    <Input value={formData.poNumber || formData.purchaseOrderId} readOnly className="bg-muted" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Input
                    value={formData.vendorName}
                    readOnly
                    className="bg-muted"
                    placeholder="Auto-filled from PO"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Delivery Date *</Label>
                  <Input
                    type="date"
                    value={formData.deliveryDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                  />
                  {errors.deliveryDate && <p className="text-sm text-destructive">{errors.deliveryDate}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Note Number</Label>
                  <Input
                    value={formData.deliveryNoteNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryNoteNumber: e.target.value }))}
                    placeholder="Supplier's delivery note reference"
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Packing Slip Number</Label>
                  <Input
                    value={formData.packingSlipNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, packingSlipNumber: e.target.value }))}
                    placeholder="Packing slip reference"
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                  />
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Delivery Details</h4>
              </div>

              <div className="grid gap-2">
                <Label>Receiving Warehouse</Label>
                <Select
                  value={formData.warehouseId}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, warehouseId: v }))}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select destination warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {(warehouses as any[]).filter((w: any) => w.warehouse_id && w.is_active !== false).map((w: any) => (
                      <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Carrier / Transporter</Label>
                  <Input
                    value={formData.carrier}
                    onChange={(e) => setFormData(prev => ({ ...prev, carrier: e.target.value }))}
                    placeholder="Carrier name"
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <Input
                    value={formData.trackingNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, trackingNumber: e.target.value }))}
                    placeholder="Shipment tracking number"
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quality Status</Label>
                  <Select
                    value={formData.qualityStatus}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, qualityStatus: v as QualityStatus }))}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending Inspection</SelectItem>
                      <SelectItem value="passed">Passed</SelectItem>
                      <SelectItem value="conditional">Conditional Pass</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="inspection-required"
                  checked={formData.inspectionRequired}
                  onChange={(e) => setFormData(prev => ({ ...prev, inspectionRequired: e.target.checked }))}
                  disabled={isReadOnly}
                  className="h-4 w-4"
                />
                <Label htmlFor="inspection-required">Inspection Required</Label>
              </div>
            </div>

            {/* Received Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Received Items</h4>
                </div>
                {!isReadOnly && mode !== 'add' && (
                  <Button variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                )}
              </div>

              {errors.items && <p className="text-sm text-destructive">{errors.items}</p>}

              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg">
                  {mode === 'add' ? 'Select a Purchase Order above to load items' : 'No items'}
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Item Description</TableHead>
                        <TableHead>Ordered Qty</TableHead>
                        <TableHead>Received Qty</TableHead>
                        <TableHead>Accepted / Rejected</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>UoM</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Inspection</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-muted-foreground text-sm">{item.line_number}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{item.item_description}</p>
                              {item.item_code && <p className="text-xs text-muted-foreground">{item.item_code}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.expected_quantity}
                              onChange={(e) => updateItem(index, 'expected_quantity', parseFloat(e.target.value) || 0)}
                              readOnly={isReadOnly || mode === 'add'}
                              className={`${isReadOnly || mode === 'add' ? 'bg-muted border-0' : ''} w-20`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.received_quantity}
                              onChange={(e) => updateItem(index, 'received_quantity', parseFloat(e.target.value) || 0)}
                              readOnly={isReadOnly}
                              className={`${isReadOnly ? 'bg-muted border-0' : ''} w-20`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                value={item.accepted_quantity}
                                onChange={(e) => updateItem(index, 'accepted_quantity', parseFloat(e.target.value) || 0)}
                                readOnly={isReadOnly}
                                className={`${isReadOnly ? 'bg-muted border-0' : ''} w-16 text-green-700`}
                                placeholder="Acc"
                              />
                              <Input
                                type="number"
                                value={item.rejected_quantity}
                                onChange={(e) => updateItem(index, 'rejected_quantity', parseFloat(e.target.value) || 0)}
                                readOnly={isReadOnly}
                                className={`${isReadOnly ? 'bg-muted border-0' : ''} w-16 text-red-700`}
                                placeholder="Rej"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1 min-w-[80px]">
                              <Progress value={getCompletionPercentage(item.expected_quantity, item.received_quantity)} className="h-2" />
                              <p className="text-xs text-muted-foreground">
                                {getCompletionPercentage(item.expected_quantity, item.received_quantity).toFixed(0)}%
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.unit_of_measure}
                              readOnly
                              className="bg-muted border-0 w-16 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.unit_cost || ''}
                              onChange={(e) => updateItem(index, 'unit_cost', parseFloat(e.target.value) || undefined)}
                              readOnly={isReadOnly}
                              className={`${isReadOnly ? 'bg-muted border-0' : ''} w-24 text-xs`}
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={item.inspection_result}
                              onValueChange={(v) => updateItem(index, 'inspection_result', v)}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className="w-28 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_required">Not Required</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="passed">Passed</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                                <SelectItem value="rework_required">Rework</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.quality_notes}
                              onChange={(e) => updateItem(index, 'quality_notes', e.target.value)}
                              placeholder="Notes..."
                              readOnly={isReadOnly}
                              className={`${isReadOnly ? 'bg-muted border-0' : ''} text-xs`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Additional Information</h4>
              </div>

              <div className="space-y-2">
                <Label>Special Instructions</Label>
                <Textarea
                  value={formData.specialInstructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialInstructions: e.target.value }))}
                  placeholder="Special handling instructions..."
                  rows={2}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label>General Notes / Remarks</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes about the delivery, condition, or issues..."
                  rows={3}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {mode === 'add' ? 'Create GRN' : 'Update GRN'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Quality Inspection Modal ─────────────────────────────────────────────────

function QualityInspectionModal({ grn, trigger, onSaved }: { grn: ReceivingOrderSummary; trigger: React.ReactNode; onSaved?: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qualityStatus, setQualityStatus] = useState<QualityStatus>(grn.quality_status);
  const [comments, setComments] = useState('');

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await authService.getUserProfile();
      await procurementService.receivingWorkflowAction(grn.receiving_order_id, {
        action: 'approve',
        quality_status: qualityStatus,
        comments: comments || undefined,
      });
      showSuccess('Quality inspection recorded');
      setOpen(false);
      onSaved?.();
    } catch (err: any) {
      showError(err?.message || 'Failed to record inspection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Quality Inspection – {grn.receiving_number}
          </DialogTitle>
          <DialogDescription>Record quality inspection result for this receiving order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Quality Status *</Label>
            <Select value={qualityStatus} onValueChange={(v) => setQualityStatus(v as QualityStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="conditional">Conditional Pass</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Inspection Notes</Label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Notes from the quality inspection..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
            Submit Inspection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main GoodsReceived Component ─────────────────────────────────────────────

export function GoodsReceived() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterQuality, setFilterQuality] = useState('all');
  const [grns, setGrns] = useState<ReceivingOrderSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const loadGRNs = useCallback(async () => {
    setLoading(true);
    try {
      await authService.getUserProfile();
      const res = await procurementService.getReceivingOrders({ limit: 200 });
      if (res.data) {
        setGrns(Array.isArray(res.data) ? res.data : []);
      }
    } catch {
      showError('Failed to load Goods Received Notes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGRNs();
  }, [loadGRNs]);

  const filteredGRNs = useMemo(() => {
    return grns.filter(grn => {
      const matchesSearch =
        grn.receiving_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (grn.po_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        grn.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (grn.created_by_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || grn.status === filterStatus;
      const matchesQuality = filterQuality === 'all' || grn.quality_status === filterQuality;
      return matchesSearch && matchesStatus && matchesQuality;
    });
  }, [searchTerm, filterStatus, filterQuality, grns]);

  const getStatusBadge = (status: ReceivingStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Clock className="h-3 w-3 mr-1" />In Progress</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'on_hold':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />On Hold</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><X className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getQualityBadge = (quality: QualityStatus) => {
    switch (quality) {
      case 'passed':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Passed</Badge>;
      case 'conditional':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><AlertTriangle className="h-3 w-3 mr-1" />Conditional</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><X className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><X className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{quality}</Badge>;
    }
  };

  const stats = useMemo(() => ({
    total: grns.length,
    completed: grns.filter(g => g.status === 'completed').length,
    pending: grns.filter(g => g.status === 'pending').length,
    inProgress: grns.filter(g => g.status === 'in_progress').length,
    withDiscrepancies: grns.filter(g => g.has_discrepancies).length,
  }), [grns]);

  const handleWorkflowAction = async (grn: ReceivingOrderSummary, action: 'start' | 'complete' | 'hold' | 'cancel') => {
    try {
      await authService.getUserProfile();
      await procurementService.receivingWorkflowAction(grn.receiving_order_id, { action });
      showSuccess(`GRN ${action === 'start' ? 'started' : action === 'complete' ? 'completed' : action === 'hold' ? 'placed on hold' : 'cancelled'}`);
      loadGRNs();
    } catch (err: any) {
      showError(err?.message || `Failed to ${action} GRN`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold">Goods Received Notes</h2>
          <p className="text-muted-foreground">Track material deliveries and receipt confirmations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadGRNs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <GRNModal
            mode="add"
            onSaved={loadGRNs}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create GRN
              </Button>
            }
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Truck className="h-4 w-4 text-blue-600" />
              Total GRNs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">All goods received</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
            <p className="text-xs text-green-600 mt-1">Fully processed</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending / In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending + stats.inProgress}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting processing</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              With Discrepancies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.withDiscrepancies}</div>
            <p className="text-xs text-red-600 mt-1">Require attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by GRN number, PO, supplier, or creator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterQuality} onValueChange={setFilterQuality}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quality</SelectItem>
                  <SelectItem value="pending">Pending QC</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GRNs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Goods Received Notes</CardTitle>
          <CardDescription>
            Showing {filteredGRNs.length} of {grns.length} goods received notes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading GRNs...</span>
            </div>
          ) : filteredGRNs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Truck className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No goods received notes found</p>
              <p className="text-sm mt-1">Create a GRN to record incoming deliveries</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>GRN Details</TableHead>
                    <TableHead>Supplier & PO</TableHead>
                    <TableHead>Received By</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Quality & Discrepancies</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGRNs.map(grn => (
                    <TableRow key={grn.receiving_order_id} className="hover:bg-muted/50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{grn.receiving_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {grn.actual_delivery_date || grn.expected_delivery_date || '—'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Created {new Date(grn.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{grn.vendor_name}</p>
                          <p className="text-sm text-muted-foreground">{grn.po_number || '—'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{grn.created_by_name || '—'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium">{grn.items_count}</span>
                          <span className="text-muted-foreground"> items</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {getQualityBadge(grn.quality_status)}
                          {grn.has_discrepancies && (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />Discrepancies
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{grn.location_name || '—'}</p>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(grn.status)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <GRNModal
                              mode="view"
                              grn={grn}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                              }
                            />
                            {(grn.status === 'pending' || grn.status === 'in_progress' || grn.status === 'on_hold') && (
                              <GRNModal
                                mode="edit"
                                grn={grn}
                                onSaved={loadGRNs}
                                trigger={
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit GRN
                                  </DropdownMenuItem>
                                }
                              />
                            )}
                            {grn.status === 'pending' && (
                              <DropdownMenuItem onClick={() => handleWorkflowAction(grn, 'start')}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Start Receiving
                              </DropdownMenuItem>
                            )}
                            {grn.status === 'in_progress' && (
                              <>
                                <DropdownMenuItem onClick={() => handleWorkflowAction(grn, 'complete')}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Complete Receiving
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleWorkflowAction(grn, 'hold')}>
                                  <AlertTriangle className="h-4 w-4 mr-2" />
                                  Place on Hold
                                </DropdownMenuItem>
                              </>
                            )}
                            <QualityInspectionModal
                              grn={grn}
                              onSaved={loadGRNs}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <ClipboardCheck className="h-4 w-4 mr-2" />
                                  Quality Inspection
                                </DropdownMenuItem>
                              }
                            />
                            <DropdownMenuItem>
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Printer className="h-4 w-4 mr-2" />
                              Print GRN
                            </DropdownMenuItem>
                            {grn.status !== 'completed' && grn.status !== 'cancelled' && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleWorkflowAction(grn, 'cancel')}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Cancel GRN
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
